import React, { useState } from "react";
import { motion } from "framer-motion";
import { PenTool, Download, Sparkles, UserCheck, Smartphone, CheckSquare } from "lucide-react";

export default function SaasSubjectiveExams() {
  const [evaluationMode, setEvaluationMode] = useState("ai");

  return (
    <section className="py-24 bg-white dark:bg-slate-950 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          
          {/* Left Column: Digital tablet preview */}
          <div className="lg:col-span-7 flex justify-center">
            
            {/* Tablet container */}
            <div className="bg-slate-900 p-5 rounded-[2.5rem] border border-slate-700 shadow-2xl w-full max-w-lg aspect-[4/3] flex flex-col justify-between overflow-hidden relative">
              {/* Screen gloss highlights */}
              <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />

              {/* Title Header bar */}
              <div className="flex justify-between items-center border-b border-slate-800 pb-3 z-10">
                <div className="flex items-center gap-2">
                  <PenTool size={16} className="text-violet-400" />
                  <span className="text-[10px] font-black uppercase text-slate-300">Written Exam Sheet</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="px-2 py-0.5 bg-slate-800 text-slate-400 rounded text-[8px] font-bold">
                    Paper ID: #PHYS-920
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-[8px] text-slate-500 font-bold">Timer: 45m left</span>
                  </div>
                </div>
              </div>

              {/* Canvas writing area (simulate handwritten maths) */}
              <div className="flex-1 bg-amber-50/95 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl m-2 p-5 relative overflow-hidden flex flex-col justify-between">
                
                {/* Question label */}
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Question 2</p>
                  <p className="text-xs font-black text-slate-800 dark:text-white mt-1 leading-snug">
                    State the Work-Energy Theorem and prove it using integral calculus.
                  </p>
                </div>

                {/* Animated Handwriting Paths */}
                <div className="my-6 flex-1 relative flex items-center justify-center">
                  <svg viewBox="0 0 200 80" className="w-full max-w-sm h-full opacity-80">
                    {/* Simulated student formulas drawing in real time */}
                    <motion.path
                      d="M 10,20 L 40,20 L 40,23 M 30,20 L 30,50 M 50,35 L 70,35 M 60,25 L 60,45"
                      fill="none"
                      stroke="#4f46e5"
                      strokeWidth="2"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      whileInView={{ pathLength: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 2.5, ease: "easeInOut" }}
                    />
                    <motion.path
                      d="M 90,35 L 105,35 M 100,25 Q 110,45 120,35 T 140,35"
                      fill="none"
                      stroke="#4f46e5"
                      strokeWidth="2"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      whileInView={{ pathLength: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 2.5, delay: 1, ease: "easeInOut" }}
                    />
                    {/* Text writing simulated */}
                    <motion.text
                      x="10"
                      y="65"
                      fill="#4f46e5"
                      fontSize="9"
                      fontFamily="cursive"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 2 }}
                    >
                      Hence, W = Kf - Ki (Proved)
                    </motion.text>
                  </svg>

                  {/* Stylus pointer indicator */}
                  <motion.div
                    animate={{
                      x: [-120, -50, 20, 80, -120],
                      y: [30, -10, 10, 40, 30]
                    }}
                    transition={{
                      duration: 6,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="absolute top-0 left-1/2 w-4 h-4 text-violet-600 pointer-events-none"
                  >
                    ✏️
                  </motion.div>
                </div>

                {/* Bottom Canvas Tools */}
                <div className="flex justify-between items-center border-t border-slate-100 dark:border-slate-800 pt-3 text-[8px] text-slate-500 font-bold uppercase">
                  <div className="flex gap-2">
                    <span className="text-violet-600">✍️ Stylus Active</span>
                    <span>Pressure: 82%</span>
                  </div>
                  <span>Page 1 of 3</span>
                </div>

              </div>

              {/* Tablet Bottom Bezel Button */}
              <div className="flex justify-center pb-1">
                <div className="w-10 h-10 rounded-full border border-slate-800 bg-slate-950 flex items-center justify-center text-slate-600 text-xs shadow-inner">
                  ◯
                </div>
              </div>

            </div>
          </div>

          {/* Right Column: Explanations */}
          <div className="lg:col-span-5 space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 dark:bg-purple-950/40 border border-purple-100 dark:border-purple-900/50 rounded-full text-purple-600 dark:text-purple-400 text-sm font-bold shadow-sm">
              <PenTool size={16} />
              <span>Subjective Exams Support</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white leading-tight tracking-tight">
              Evaluate Written Answers Digitally
            </h2>
            
            <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed font-semibold">
              Don't limit students to MCQs. Skillzy allows learners to upload written scripts or use digital styluses to answer subjective questions. Evaluators grade instantly with AI-supported marking frameworks.
            </p>

            {/* Feature buttons */}
            <div className="space-y-4 pt-4 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-start gap-3">
                <CheckSquare size={20} className="text-purple-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-extrabold text-slate-850 dark:text-white">Stylus & Multi-Touch Canvas</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Draw complex diagrams, solve math calculations, or write essays using interactive canvas boards.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Download size={20} className="text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-extrabold text-slate-850 dark:text-white">One-Click PDF Script Export</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Export student exam scripts with full annotations, remarks, signatures, and grade summaries instantly.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Sparkles size={20} className="text-violet-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-extrabold text-slate-850 dark:text-white">AI-Drafted Grading Assistance</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Evaluate essays against pre-fed rubrics. AI suggests scores and flags critical missing terms.
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
